import React from 'react'
import {useSelector} from 'react-redux'
import {STEPS} from '../../../../app-store/AccountSetupSlice'
import {selectCompletedSteps} from '../../../../app-store/AccountSetupSlice'
import {
  selectLocations,
  selectOrderMode,
  selectSelectedLocationId,
} from '../../../../app-store/PurchaseLinkSlice'
import {getSchedulingLocations} from '../utils/getSchedulingLocations'
import {selectEffectiveRole} from '../../../../app-store/selector'
import {
  StepperWrapper,
  StepItem,
  StepButton,
  StepCircle,
  StepMeta,
  StepLabel,
  StepSubtitle,
  Connector,
} from './Stepper.styles'

function CheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M3 8l3.5 3.5L13 5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function EditIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <path
        d="M11.3 1.7a1.5 1.5 0 0 1 2.1 0l.9.9a1.5 1.5 0 0 1 0 2.1l-7.6 7.6-3.7.9.9-3.7 7.4-7.8z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10 3l3 3"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/**
 * Stepper
 *
 * @param {number}   currentStep  - active step index
 * @param {Function} onStepClick  - called with index when a completed step is clicked
 */
export function Stepper({
  currentStep,
  // onStepClick
}) {
  const completedSteps = useSelector(selectCompletedSteps)
  const locations = useSelector(selectLocations)
  const orderMode = useSelector(selectOrderMode)
  const selectedLocationId = useSelector(selectSelectedLocationId)
  const roles = useSelector(selectEffectiveRole)
  // The catalog is role-scoped before it reaches this wizard. Apply the same
  // product rule used by Catalog Continue, so Scheduling is visible only when
  // this role's selected plans require a call for at least one order location.
  const schedulingRequired =
    getSchedulingLocations({
      locations,
      orderMode,
      selectedLocationId,
      roles,
    }).length > 0
  const visibleSteps = STEPS.map((step, index) => ({step, index})).filter(
    ({step}) => step.id !== 'scheduling' || schedulingRequired,
  )

  const getStatus = (index, stepId) => {
    if (index === currentStep) return 'active'
    if (completedSteps.includes(stepId)) return 'completed'
    return 'inactive'
  }

  return (
    <StepperWrapper aria-label="Setup progress">
      {visibleSteps.map(({step, index}, visibleIndex) => {
        const status = getStatus(index, step.id)
        const isCompleted = status === 'completed'

        return (
          <StepItem key={step.id}>
            <StepButton
              isCompleted={isCompleted}
              // onClick={() => isCompleted && onStepClick(index)}
              aria-current={status === 'active' ? 'step' : undefined}
              aria-label={`${step.label} — ${status}`}
            >
              <StepCircle status={status}>
                {isCompleted ? (
                  <CheckIcon />
                ) : status === 'active' ? (
                  <EditIcon />
                ) : (
                  visibleIndex + 1
                )}
              </StepCircle>

              <StepMeta>
                <StepLabel status={status}>{step.label}</StepLabel>
                <StepSubtitle>{step.subtitle}</StepSubtitle>
              </StepMeta>
            </StepButton>

            {visibleIndex < visibleSteps.length - 1 && (
              <Connector aria-hidden="true" />
            )}
          </StepItem>
        )
      })}
    </StepperWrapper>
  )
}
